import InventoryStore from 'admin/InventoryStore'
import InventoryItem from 'admin/inventory/InventoryItem'
import InventoryItemRequestQueryFilterBuilder, {
  InventoryItemArchivedFilterOptions,
  InventoryItemProVisibilityFilterOptions,
} from 'admin/inventory/InventoryItemRequestQueryFilterBuilder'
import ModalViewModel from 'infra/ModalViewModel'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { action, makeObservable, observable } from 'mobx'
import { switchMap } from 'rxjs'
import { Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { shareLatest } from 'utils/rx-util'
import {
  JobScopeCustomItem,
  JobScopeItemSource,
  JobScopeInventoryItem,
  CustomItemsToRemove,
} from '../UpdateJobScopeViewModel'
import { task } from 'mobx-task'
import { shallowItemsAreEqual } from 'utils/array-util'
import Job from 'jobs/Job'

export enum ManageItemsMode {
  AnExistingService,
  NewCustomService,
}

export default class ManageItemsViewModel extends ModalViewModel {
  searchText = new SearchTextViewModel()

  @observable
  mode = ManageItemsMode.AnExistingService

  @observable
  inventoryItems: InventoryItem[] = []

  @observable
  selectedInventoryItems: JobScopeInventoryItem[] = []

  @observable
  selectedCustomItems: JobScopeCustomItem[] = []

  @observable
  selectedCustomItemsToRemove: CustomItemsToRemove[] = []

  constructor(
    private inventoryStore: InventoryStore,
    private job: Job,
    private workspaceId: Id,
    private addSelectedInventoryItem: (item: JobScopeInventoryItem) => void,
    private setAddedInventoryItems: (items: JobScopeInventoryItem[]) => void,
    private setSelectedCustomItem: (items: JobScopeCustomItem[]) => void,
    private getAllCurrentItemsForManageItemsVM: () => void,
    private addCustomItemToRemove: (id: string) => void,
  ) {
    super()
    makeObservable(this)

    this.query$.pipe(this.whileShowing()).subscribe()
    this.close$.subscribe(this.reset)
  }

  @action.bound
  setInventoryItems(inventoryItems: InventoryItem[]) {
    if (shallowItemsAreEqual(this.inventoryItems, inventoryItems)) {
      return
    }
    this.inventoryItems = inventoryItems
  }

  @action.bound
  setSelectedInventoryItems(inventoryItems: JobScopeInventoryItem[]) {
    if (shallowItemsAreEqual(this.selectedInventoryItems, inventoryItems)) {
      return
    }
    this.selectedInventoryItems = inventoryItems
  }

  @action.bound
  setSelectedCustomItems(customItems: JobScopeCustomItem[]) {
    if (shallowItemsAreEqual(this.selectedCustomItems, customItems)) {
      return
    }
    this.selectedCustomItems = customItems
  }

  @action.bound
  createCustomItem() {
    const newCustomItem = new JobScopeCustomItem(JobScopeItemSource.Added)
    this.selectedCustomItems.push(newCustomItem)
  }

  @action.bound
  addSelectedCustomItemToRemove(customItem: CustomItemsToRemove) {
    this.selectedCustomItemsToRemove.push(customItem)
  }

  @action.bound
  removeSelectedCustomItem(customInventoryItem: JobScopeCustomItem) {
    if (customInventoryItem.source === JobScopeItemSource.Job) {
      this.addSelectedCustomItemToRemove({
        id: customInventoryItem.id,
      })
    }

    this.selectedCustomItems = this.selectedCustomItems.filter(
      (item) => item !== customInventoryItem,
    )
  }

  @action.bound
  reset() {
    this.searchText.clear()
    this.mode = ManageItemsMode.AnExistingService
    this.selectedInventoryItems = []
    this.selectedCustomItems = []
    this.selectedCustomItemsToRemove = []
    this.inventoryItems = []

    this.getAllCurrentItemsForManageItemsVM()
  }

  @action.bound
  setMode(mode: ManageItemsMode) {
    this.mode = mode
  }

  onChangeMode(mode: ManageItemsMode) {
    this.setMode(mode)
  }

  submit = task.resolved(() => {
    let hasValidationErrors = false
    this.selectedCustomItems.forEach((item) => {
      if (!item.validate()) {
        hasValidationErrors = true
      }
    })
    if (hasValidationErrors) {
      return
    }

    this.inventoryItems.forEach((item) => {
      const inventoryItem = new JobScopeInventoryItem(item, (i) => {
        this.setAddedInventoryItems(
          this.selectedInventoryItems.filter(
            (selectedItem) => selectedItem.versionId !== i.versionId,
          ),
        )
      })
      this.addSelectedInventoryItem(inventoryItem)
    })

    this.selectedCustomItemsToRemove.forEach((item) => {
      this.addCustomItemToRemove(item.id)
    })

    this.setSelectedCustomItem(this.selectedCustomItems.slice())

    this.close()
  })

  query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() => this.inventoryItemScrollProvider.fetch()),
    shareLatest(),
  )

  inventoryItemScrollProvider = new InfiniteScrollProvider<InventoryItem>({
    limit: 50,
    startFetchTaskAsPending: true,
    fetchItems: async (paging) => {
      const results = await this.inventoryStore.fetchInventoryItems(
        this.workspaceId ? [this.workspaceId] : [],
        paging.limit ?? 50,
        this.searchText.value,
        paging.after ?? undefined,
        new InventoryItemRequestQueryFilterBuilder()
          .withArchivedStatus(InventoryItemArchivedFilterOptions.ACTIVE)
          .withProVisibilityStatus(InventoryItemProVisibilityFilterOptions.ALL)
          .forJobType(this.job.jobType)
          .build(),
        'name',
      )

      const filteredResults = results.data.filter(
        (item) =>
          !this.selectedInventoryItems.find(
            (selectedItem) => selectedItem.versionId === item.versionId,
          ),
      )

      return {
        data: filteredResults,
        cursor: results.cursor,
      }
    },
  })
}
